import { useState, useEffect } from "react";
import { firestore } from "../firebase"; // Ensure Firestore is configured
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import "./admin-view.css";

function AdminView() {
  const auth = getAuth();
  const [aboutImages, setAboutImages] = useState([
    {
      mainText: "",
      image1: "",
      image2: "",
      image3: "",
    },
  ]);
  const [caseStudies, setCaseStudies] = useState([
    { heading: "", subHeading: "", isDark: true, link: "", image: null },
    { heading: "", subHeading: "", isDark: true, link: "", image: null },
  ]);
  const [homeData, setHomeData] = useState({
    heading: "",
    subHeading: "",
    paragraph: "",
    button1Text: "",
    button1Theme: false,
    button2Text: "",
    button2Theme: false,
  });
  const [aboutText, setAboutText] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Track authentication state
  const [email, setEmail] = useState(""); // Email input state
  const [password, setPassword] = useState(""); // Password input state
  const [error, setError] = useState("");

  const homeRef = collection(firestore, "home");
  const aboutRef = collection(firestore, "about");
  const caseStudyRef = collection(firestore, "caseStudies");

  setPersistence(auth, browserSessionPersistence)
    .then(() => {})
    .catch(() => {
      console.error("Error setting persistence");
    });

  const compressImage = (file, maxWidth = 800, maxHeight = 800) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Resize the image to fit within the max dimensions
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);

          // Convert the canvas to a base64 string
          canvas.toBlob(
            (blob) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            },
            "image/jpeg",
            0.7 // Compression quality (0.7 = 70%)
          );
        };
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    // Check authentication state
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe(); // Cleanup the listener on unmount
  }, [auth]);

  useEffect(() => {
    if (!isAuthenticated) return; // Only fetch data if the user is authenticated

    // Fetch initial data from Firestore
    const fetchData = async () => {
      try {
        // Fetch home data
        const homeSnapshot = await getDocs(homeRef);
        const homeDoc = homeSnapshot.docs[0]; // Assuming only one document exists
        if (homeDoc) {
          setHomeData(homeDoc.data());
        }

        // Fetch about data
        const aboutSnapshot = await getDocs(aboutRef);
        const aboutDoc = aboutSnapshot.docs[0]; // Assuming only one document exists
        if (aboutDoc) {
          const aboutData = aboutDoc.data();
          setAboutText(aboutData.text);
          setAboutImages(aboutData.images || [null, null, null]);
        }

        // Fetch case studies
        const caseStudySnapshot = await getDocs(caseStudyRef);
        const caseStudyDoc = caseStudySnapshot.docs[0]; // Assuming only one document exists
        if (caseStudyDoc) {
          setCaseStudies(caseStudyDoc.data().caseStudies || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const sanitizeInput = (input) => {
    // Remove any potentially malicious characters
    const sanitizedInput = input.replace(/[<>]/g, "");
    return sanitizedInput.trim(); // Remove leading/trailing whitespace
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); // Clear any previous error messages

    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, sanitizedEmail, sanitizedPassword); // Firebase login
      setIsAuthenticated(true); // Set authentication state to true
    } catch (err) {
      setError("Invalid email or password. Please try again."); // Display error message
    }
  };

  const handleHomeSave = async (e) => {
    e.preventDefault();
    try {
      const homeDoc = doc(firestore, "homeHeading", "2P3yHE6HyaMVqIJzkqrc"); // Replace with your document ID
      await updateDoc(homeDoc, homeData);
      alert("Home data updated successfully!");
    } catch (error) {
      console.error("Error updating home data:", error);
    }
  };

  const handleAboutSave = async (e) => {
    e.preventDefault();
    try {
      const aboutDoc = doc(firestore, "homeHeading", "AajyC12TkxFffgcwOasa"); // Replace with your document ID
      const compressedImages = await Promise.all(
        aboutImages.map((image) => (image ? compressImage(image) : null))
      );
      await updateDoc(aboutDoc, { text: aboutText, images: compressedImages });
      alert("About data updated successfully!");
    } catch (error) {
      console.error("Error updating about data:", error);
    }
  };

  const handleCaseStudySave = async (e) => {
    e.preventDefault();
    try {
      const caseStudyDoc = doc(
        firestore,
        "homeHeading",
        "0NSvm9rQvBUTOkBuhygW"
      ); // Replace with your document ID
      const updatedCaseStudies = await Promise.all(
        caseStudies.map(async (caseStudy) => {
          if (caseStudy.image) {
            const compressedImage = await compressImage(caseStudy.image);
            return { ...caseStudy, image: compressedImage };
          }
          return caseStudy;
        })
      );
      await updateDoc(caseStudyDoc, { caseStudies: updatedCaseStudies });
      alert("Case studies updated successfully!");
    } catch (error) {
      console.error("Error updating case studies:", error);
    }
  };

  const handleCaseStudyChange = (index, field, value) => {
    const updatedCaseStudies = [...caseStudies];
    updatedCaseStudies[index][field] = value;
    setCaseStudies(updatedCaseStudies);
  };

  if (!isAuthenticated) {
    return (
      <div className="login-form">
        <h1>Login</h1>
        <form onSubmit={handleLogin}>
          <label>Email:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label>Password:</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <p className="error-message">{error}</p>}{" "}
          {/* Display error message */}
          <button type="submit">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <h1>Admin View</h1>

      {/* Home Section */}
      <form onSubmit={handleHomeSave} className="admin-form">
        <h2>Edit Home Section</h2>
        <label>Heading:</label>
        <input
          type="text"
          value={homeData.heading}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, heading: e.target.value }))
          }
        />
        <label>Sub Heading:</label>
        <input
          type="text"
          value={homeData.subHeading}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, subHeading: e.target.value }))
          }
        />
        <label>Paragraph:</label>
        <textarea
          value={homeData.paragraph}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, paragraph: e.target.value }))
          }
        ></textarea>
        <label>Button 1 Text:</label>
        <input
          type="text"
          value={homeData.button1Text}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, button1Text: e.target.value }))
          }
        />
        <label>Button 2 Text:</label>
        <input
          type="text"
          value={homeData.button2Text}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, button2Text: e.target.value }))
          }
        />
        <label>Theme:</label>
        <select
          value={homeData.theme}
          onChange={(e) =>
            setHomeData((prev) => ({ ...prev, theme: e.target.value }))
          }
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
        <button type="submit">Save Home Section</button>
      </form>

      {/* About Section */}
      <form onSubmit={handleAboutSave} className="admin-form">
        <h2>Edit About Section</h2>
        <label>Main Text:</label>
        <textarea
          value={aboutText}
          onChange={(e) => setAboutText(e.target.value)}
        ></textarea>
        <label>Replace Images:</label>
        <div className="d-flex flex-column image-upload-container">
          {[0, 1, 2].map((index) => (
            <div className="col-12" key={index}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setAboutImages((prev) => {
                    const updated = [...prev];
                    updated[index] = e.target.files[0];
                    return updated;
                  })
                }
              />
            </div>
          ))}
        </div>
        <button type="submit">Save About Section</button>
      </form>

      {/* Case Study Section */}
      <form onSubmit={handleCaseStudySave} className="admin-form">
        <h2>Edit Case Studies</h2>
        {caseStudies.map((caseStudy, index) => (
          <div key={index} className="case-study-form">
            <label>Heading:</label>
            <input
              type="text"
              value={caseStudy.heading}
              onChange={(e) =>
                handleCaseStudyChange(index, "heading", e.target.value)
              }
            />
            <label>Sub Heading:</label>
            <input
              type="text"
              value={caseStudy.subHeading}
              onChange={(e) =>
                handleCaseStudyChange(index, "subHeading", e.target.value)
              }
            />
            <label>Button Theme:</label>
            <select
              value={caseStudy.isDark ? "dark" : "light"}
              onChange={(e) =>
                handleCaseStudyChange(
                  index,
                  "isDark",
                  e.target.value === "dark"
                )
              }
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
            <label>Button Link:</label>
            <input
              type="text"
              value={caseStudy.link}
              onChange={(e) =>
                handleCaseStudyChange(index, "link", e.target.value)
              }
            />
            <label>Image:</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleCaseStudyChange(index, "image", e.target.files[0])
              }
            />
          </div>
        ))}
        <button type="submit">Save Case Studies</button>
      </form>
    </div>
  );
}

export default AdminView;
